import { useParams, useSearchParams } from 'react-router-dom'

import { BlankSlate, Box, Button, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsEmployeePageDocument,
  PerformanceGoalsEmployeePageMetricGroupsDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { InfiniteLoader, usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { CreateMetricModal } from 'modules/goals/components/CreateMetricModal'
import { EditGoalsWeightsModal } from 'modules/goals/components/EditGoalsWeightsModal'
import { MetricList } from 'modules/goals/components/MetricList'
import { ProgressSummary } from 'modules/goals/components/ProgressSummary'
import { UpdateMetricModal } from 'modules/goals/components/UpdateMetricModal'
import { normalizeMetric } from 'modules/goals/lib/normalizeMetric'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { CycleFilter } from 'modules/performance_cycles/components/CycleFilter'
import { useCycleFilter } from 'modules/performance_cycles/hooks/useCycleFilter'

const EmployeePage = () => {
  const [open, toggle] = useToggle(false)

  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { navigation } = useNavigation()
  const { cycle, loading: loadingCycles, props: cycleFilterProps } = useCycleFilter()

  const cycleIds = compact([cycleFilterProps.value])

  const openCreateDialog = () => setSearchParams({ modal: 'create' })
  const openEditWeightsModal = () => setSearchParams({ modal: 'weights' })

  const { data, loading, error } = useQuery(PerformanceGoalsEmployeePageDocument, {
    variables: {
      id: Number(params.id),
      noCycle: cycleFilterProps.noCycleSelected,
      cycleIds,
    },
  })

  const {
    nodes: metricGroups,
    data: metricGroupsData,
    loading: loadingMetricGroups,
    error: metricGroupsError,
    controls,
  } = usePaginatedQuery(PerformanceGoalsEmployeePageMetricGroupsDocument, {
    variables: {
      id: Number(params.id),
      noCycle: cycleFilterProps.noCycleSelected,
      cycleIds,
    },
    extractConnection: ({ goals }) => goals?.metricGroupsConnection,
  })

  const metricGroupsCount = metricGroupsData?.goals.metricGroupsConnection?.totalCount ?? 0
  const anyWeightedMetricGroup = metricGroups.some(({ weights }) => weights.length > 0)

  const goalsEmployee = data?.goals?.employeesConnection?.nodes?.[0]
  const employee = goalsEmployee?.employee

  const metricSummary = goalsEmployee?.metricSummary
  const cycleProgress = goalsEmployee?.progresses?.[0]

  if (!params.id) throw 'No ID provided'

  const allMetrics = metricGroups.flatMap((group) => group.metrics)
  const editingMetric = allMetrics.find((metric) => metric.id === searchParams.get('id'))

  // Goals can be weighted if there is a cycle selected and the user has permission
  // If the assignee has less than two metric groups in the cycle, the button should be disabled
  const canWeightGoals = employee && cycle && cycleProgress?._policies?.upsertWeights?.can
  const hasLessThanTwoMetricGroups = metricGroupsCount < 2

  return (
    <ResponsivePage.Body
      loading={loading || loadingMetricGroups || loadingCycles}
      error={error || metricGroupsError}
      showHeader={false}
      header={{
        widget: (
          <>
            {goalsEmployee?._policies?.createMetric?.can && (
              <Button iconName="add" onClick={openCreateDialog}>
                {i18n.t('performance_goals.pages.employee_page.create_button')}
              </Button>
            )}
          </>
        ),
      }}
    >
      <Box gap="s40">
        <Box flexDirection="row" justifyContent="spaceBetween">
          {goalsEmployee && (
            <Box maxWidth="s400" flexGrow>
              {(cycleProgress || metricSummary) && (
                <ProgressSummary
                  showLabel
                  progress={cycleProgress?.progress ?? metricSummary?.averageProgress ?? 0}
                  weights={cycleProgress?.weights}
                />
              )}
            </Box>
          )}
          <Box justifyContent="flexEnd" flexDirection="row" gap="s8">
            <Box>
              <CycleFilter {...cycleFilterProps} />
            </Box>
            {canWeightGoals && (
              <Box>
                <Tooltip.Alt
                  attachment="top center"
                  open={open && hasLessThanTwoMetricGroups}
                  toggle={toggle}
                  label={i18n.t('performance_goals.weights.button_tooltip')}
                >
                  <Button
                    type="secondary"
                    onClick={openEditWeightsModal}
                    disabled={hasLessThanTwoMetricGroups}
                  >
                    {i18n.t('performance_goals.weights.edit_weights')}
                  </Button>
                </Tooltip.Alt>
              </Box>
            )}
          </Box>
        </Box>

        {allMetrics.length === 0 ? (
          <Box
            border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
            padding="s24"
            borderRadius={{ all: 'abs004' }}
          >
            <BlankSlate
              illustration="analytics-graph-lines"
              size="small"
              title={i18n.t('performance_goals.key_results_list.blank_slate.title')}
              description={i18n.t('performance_goals.key_results_list.blank_slate.description')}
            >
              <Button
                iconName="add"
                onClick={openCreateDialog}
                disabled={!goalsEmployee?._policies?.createMetric?.can}
              >
                {i18n.t('performance_goals.pages.employee_page.create_button')}
              </Button>
            </BlankSlate>
          </Box>
        ) : (
          <InfiniteLoader controls={controls}>
            <Box gap="s32">
              {metricGroups.map(({ id, title, metrics, weights }) => (
                <MetricList
                  key={id}
                  title={title}
                  metrics={metrics}
                  weight={weights[0]?.weight ?? (anyWeightedMetricGroup ? 0 : undefined)}
                  to={navigation.goals.groups.group.fullPath({ id: id.toString() })}
                  refetchQueriesOnEdit={[
                    PerformanceGoalsEmployeePageDocument,
                    PerformanceGoalsEmployeePageMetricGroupsDocument,
                  ]}
                />
              ))}
            </Box>
          </InfiniteLoader>
        )}
      </Box>

      {/* TODO once we remove ResponsivePage's header, move CreateMetricModal to the Factorial One EmployeeGoalsHeader component */}
      {searchParams.get('modal') === 'create' && (
        <CreateMetricModal
          defaultValues={{ assigneeId: Number(params.id) }}
          onClose={() => setSearchParams({})}
          showMetricGroupSelection
          refetchQueries={[
            PerformanceGoalsEmployeePageDocument,
            PerformanceGoalsEmployeePageMetricGroupsDocument,
          ]}
        />
      )}

      {searchParams.get('modal') === 'edit' && editingMetric && (
        <UpdateMetricModal
          metric={normalizeMetric(editingMetric)}
          onClose={() => setSearchParams({})}
          refetchQueries={[
            PerformanceGoalsEmployeePageDocument,
            PerformanceGoalsEmployeePageMetricGroupsDocument,
          ]}
        />
      )}

      {searchParams.get('modal') === 'weights' && canWeightGoals && (
        <EditGoalsWeightsModal
          employee={employee}
          cycle={cycle}
          metricGroupsCount={metricGroupsCount}
          refetchQueries={[
            PerformanceGoalsEmployeePageDocument,
            PerformanceGoalsEmployeePageMetricGroupsDocument,
          ]}
          onClose={() => setSearchParams({})}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default EmployeePage
